import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { RouteErrorBoundary } from "@/components/ErrorBoundary";
import { SkipToContent } from "@/components/shared";
import { AnimatedRoutes, MotionProvider } from "@/components/layout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";

import { usePrefetchProducts } from "@/hooks/usePrefetchProducts";
import { useState, useEffect, lazy, Suspense } from "react";

// Lazy load Sonner toaster - only needed when showing notifications
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent = () => {
  
  // Prefetch Shopify products early to break network dependency chain
  usePrefetchProducts();
  
  return (
    <>
      <SkipToContent />
      <RouteErrorBoundary>
        <AnimatedRoutes />
      </RouteErrorBoundary>
    </>
  );
};

const App = () => {
  const [showLoading, setShowLoading] = useState(() => {
    // Only show loading screen once per session
    return !sessionStorage.getItem('hasVisited');
  });

  useEffect(() => {
    if (showLoading) {
      sessionStorage.setItem('hasVisited', 'true');
    }
  }, [showLoading]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark">
        <LanguageProvider>
          <MotionProvider>
            <TooltipProvider>
              <Suspense fallback={null}>
                <Sonner />
              </Suspense>
              
              {showLoading && (
                <LoadingScreen onComplete={() => setShowLoading(false)} />
              )}
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </TooltipProvider>
          </MotionProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
